import { Autocomplete, TextField, Typography } from "@mui/material";
import { forwardRef, useMemo } from "react";

import { useCountriesQuery } from "../../queries/countries";
import { getCountryOptions } from "../../utils/countryUtils";
import { CountryCircleFlag } from "../circle-flag/CountryCircleFlag";

export interface IUseCountryOptionsResult {
    options: Array<{
        label: string;
        value: string;
    }>;
    isLoading: boolean;
}

export interface ICountrySelectProps {
    value: string | undefined;
    label?: string;
    helperText?: string;
    error?: boolean;
    disabled?: boolean;
    disableClearable?: boolean;

    onBlur?(): void;
    onChange?(value: string | undefined): void;
    useCountryOptions?(): IUseCountryOptionsResult;
}

export const CountrySelect = forwardRef<HTMLInputElement, ICountrySelectProps>(
    (
        {
            value,
            label,
            helperText,
            error,
            disabled,
            disableClearable,
            onBlur,
            onChange,
            useCountryOptions = useCountryOptionsDefault,
        },
        ref,
    ) => {
        const { options, isLoading } = useCountryOptions();
        const selectedOption = options.find((option) => option.value === value) ?? null;

        return (
            <Autocomplete
                autoHighlight={true}
                openOnFocus={true}
                size="small"
                options={options}
                value={selectedOption}
                loading={isLoading}
                disabled={disabled}
                disableClearable={disableClearable}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={ref}
                        error={error}
                        placeholder="Select a Country"
                        label={label}
                        helperText={helperText}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: value && <CountryCircleFlag countryCode={value} />,
                        }}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: "off",
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props}>
                        <CountryCircleFlag countryCode={option.value} />
                        <Typography ml={1}>{option.label}</Typography>
                    </li>
                )}
                onBlur={onBlur}
                onChange={(_event, option) => onChange?.(option?.value)}
            />
        );
    },
);

function useCountryOptionsDefault(): IUseCountryOptionsResult {
    const { data, isLoading } = useCountriesQuery();
    return useMemo(
        () => ({ options: getCountryOptions(data?.countryByCountryCode), isLoading }),
        [data?.countryByCountryCode, isLoading],
    );
}
